import { QuoteTerm } from "./types";

// Issue 33: helpers for the section / line start and end dates. These are UTC calendar dates
// (Partner Center represents every subscription/co-term date as a UTC dateTime; renewal runs at
// 00:00 UTC the day after the term's final day). They are carried as "YYYY-MM-DD" strings with
// NO timezone conversion anywhere - `<input type="date">` is timezone-agnostic and all the maths
// below is on the string / its numeric parts, never `new Date("YYYY-MM-DD")` (which parses as UTC
// midnight and then displays in local time, shifting the day west of Greenwich).

const EASTERN_TIME_ZONE = "America/New_York";
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const isIsoDate = (value: string | null | undefined): value is string =>
  typeof value === "string" && ISO_DATE.test(value);

// Today's calendar day in America/New_York as "YYYY-MM-DD" - the default section start date.
export const todayIso = (now = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: EASTERN_TIME_ZONE, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(now);
  const get = (type: string) => parts.find(part => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
};

const parts = (iso: string) => iso.split("-").map(Number) as [number, number, number];
const daysInMonth = (year: number, month: number) => new Date(Date.UTC(year, month, 0)).getUTCDate();

// The standard subscription-length end date for a term starting on `startIso` (inclusive), i.e.
// start + 1 month - 1 day for P1M, start + 1 year - 1 day for P1YM / P1YA.
export const standardEndIso = (startIso: string, term: QuoteTerm): string => {
  if (!isIsoDate(startIso)) return startIso;
  const [y, m, d] = parts(startIso);
  // Move to the day before the same day-of-month one period later.
  const utc = Date.UTC(y, m - 1, d);
  const later = new Date(utc);
  if (term === "P1M") later.setUTCMonth(later.getUTCMonth() + 1);
  else later.setUTCFullYear(later.getUTCFullYear() + 1);
  later.setUTCDate(later.getUTCDate() - 1);
  return later.toISOString().slice(0, 10);
};

// Partner Center only accepts a custom term end date within the first term ("The custom term end
// date must be within the first term duration" - learn.microsoft.com, Align subscription end
// dates), so a hand-entered end date past the first year can't be bought as quoted; the server
// prices an annual line with such a date as one full standard term. Co-term dates come from
// Partner Center itself and P1M is already cut to the end of its first calendar month (Microsoft's
// month-end alignment), so only a manual date on a P1YM / P1YA line is affected. Returns the
// warning text, or null when there is nothing to warn about.
export const pastFirstTermWarning = (
  startIso: string | null | undefined, endIso: string | null | undefined,
  endDateSource: string | null | undefined, terms: readonly (QuoteTerm | "" | null | undefined)[],
): string | null => {
  if (endDateSource !== "manual" || !isIsoDate(startIso) || !isIsoDate(endIso)) return null;
  if (!terms.some(term => term === "P1YM" || term === "P1YA")) return null;
  const firstTermEnd = standardEndIso(startIso, "P1YA");
  if (endIso <= firstTermEnd) return null;
  return `The end date is past the first one-year term, which ends ${formatIsoDate(firstTermEnd)}. Partner Center only allows a custom end date within the first term, so annual (P1YM / P1YA) lines can't be purchased with this date; they're priced as one full year.`;
};

// Inclusive day count between two "YYYY-MM-DD" dates (both counted), or 0 when malformed.
export const inclusiveDays = (startIso: string, endIso: string): number => {
  if (!isIsoDate(startIso) || !isIsoDate(endIso)) return 0;
  const [sy, sm, sd] = parts(startIso);
  const [ey, em, ed] = parts(endIso);
  return Math.round((Date.UTC(ey, em - 1, ed) - Date.UTC(sy, sm - 1, sd)) / 86_400_000) + 1;
};

export const isBeforeIso = (a: string, b: string) => isIsoDate(a) && isIsoDate(b) && a < b;
export const isAfterIso = (a: string, b: string) => isIsoDate(a) && isIsoDate(b) && a > b;

// A start date whose calendar month is later than the month whose pricing was loaded is
// speculative. `pricingMonth` is a "YYYY-MM-DD" first-of-month; when absent the current Eastern
// month is assumed.
export const isOutsideCurrentPricingMonth = (startIso: string | null | undefined, pricingMonthIso?: string | null) => {
  if (!isIsoDate(startIso)) return false;
  const priced = isIsoDate(pricingMonthIso) ? pricingMonthIso.slice(0, 7) : todayIso().slice(0, 7);
  return startIso.slice(0, 7) > priced;
};

// "Jul 13, 2026" for display; the raw string is preserved on the wire.
export const formatIsoDate = (iso: string | null | undefined) => {
  if (!isIsoDate(iso)) return "";
  const [y, m, d] = parts(iso);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    timeZone: "UTC", month: "short", day: "numeric", year: "numeric",
  });
};

// The applicable year length (365 or 366) for a P1YA line starting on `startIso`.
export const yearDays = (startIso: string): number => {
  if (!isIsoDate(startIso)) return 365;
  const [y, m, d] = parts(startIso);
  return Math.round((Date.UTC(y + 1, m - 1, d) - Date.UTC(y, m - 1, d)) / 86_400_000);
};

// One entry per calendar month the [startIso, endIso] window touches - the mirror of the
// server's QuoteProrationCalculator.CalendarMonthSpans (P1M / P1YM proration).
export const calendarMonthSpans = (startIso: string, endIso: string): { start: string; end: string; fullDays: number }[] => {
  if (!isIsoDate(startIso) || !isIsoDate(endIso) || endIso < startIso) return [];
  const pad = (n: number) => String(n).padStart(2, "0");
  const spans: { start: string; end: string; fullDays: number }[] = [];
  let [y, m] = parts(startIso);
  let cursor = startIso;
  while (cursor <= endIso) {
    const dim = daysInMonth(y, m);
    const monthEnd = `${y}-${pad(m)}-${pad(dim)}`;
    spans.push({ start: cursor, end: monthEnd < endIso ? monthEnd : endIso, fullDays: dim });
    m += 1;
    if (m > 12) { m = 1; y += 1; }
    cursor = `${y}-${pad(m)}-01`;
  }
  return spans;
};

export { daysInMonth };
